import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Send, Share2, MessageCircle, ExternalLink, Target, Eye, Shield,
  Camera, Upload, ArrowRight,
} from 'lucide-react';
import { Card } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Checkbox } from './components/ui/checkbox';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from './components/ui/sheet';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || (
  window.location.hostname === 'localhost' ? 'http://localhost:8001' : 'https://api.heytraveler.ai'
);
const ALPHA_DOCUMENTS_ENABLED = process.env.REACT_APP_DOCUMENT_UPLOADS_ENABLED === 'true';
const INTRO_MESSAGE = "hey — I'm Alex. tell me your specialty, where, minimum gross weekly pay, preferred shift, and start timing. HeyTraveler currently searches approved Care Team Solutions opportunities, supplied through Staffing Referrals. We're not searching every agency yet.";

// ============================================================================
// Ambient Pill — header chip that opens a Sheet with detail
// ============================================================================
const AmbientPill = ({ icon: Icon, label, value, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/70 hover:bg-white border border-[#E6E6E6] hover:border-[#D0CFCB] transition-all text-xs text-[#3B3A39] shadow-sm hover:shadow"
  >
    <Icon className="h-3.5 w-3.5 text-[#6B6A68]" strokeWidth={1.8} />
    <span className="lowercase font-medium">{label}</span>
    {value !== null && value !== undefined && (
      <span className="text-[#8B8A88] tabular-nums">{value}</span>
    )}
  </button>
);

// ============================================================================
// Ambient Sheets — Goal / Watches / Wallet
// ============================================================================
const GoalSheet = ({ open, onOpenChange, summary }) => {
  const goal = summary?.active_goal;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="lowercase text-[#3B3A39] font-serif" style={{ fontFamily: "'DM Serif Display', ui-serif, Georgia, serif" }}>your career goal</SheetTitle>
          <SheetDescription className="lowercase text-[#6B6A68]">optional. only if you want me to work backward from a dream.</SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-4" data-testid="goal-sheet-content">
          {goal ? (
            <Card className="p-4 border-[#E6E6E6] rounded-xl bg-[#FBFBF9]">
              <div className="text-xs uppercase tracking-wide text-[#8B8A88] mb-2">dream role</div>
              <div className="text-base text-[#3B3A39] mb-3">{goal.dream_role || 'not set'}</div>
              {goal.timeline_years && (
                <div className="text-sm text-[#6B6A68]">timeline: {goal.timeline_years} years</div>
              )}
              {goal.current_role && (
                <div className="text-sm text-[#6B6A68]">current: {goal.current_role}</div>
              )}
              {goal.priorities && goal.priorities.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs uppercase tracking-wide text-[#8B8A88] mb-1">priorities</div>
                  <div className="flex flex-wrap gap-1.5">
                    {goal.priorities.map((p, i) => (
                      <span key={i} className="px-2 py-0.5 text-xs rounded-full bg-white border border-[#E6E6E6] text-[#6B6A68]">{p}</span>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          ) : (
            <div className="text-sm text-[#6B6A68] leading-relaxed">
              nothing here yet. if you ever want to share where you&apos;re headed — CRNA path, going permanent, retirement timeline — just mention it in chat. I&apos;ll remember.
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

const WatchesSheet = ({ open, onOpenChange, summary }) => {
  const intents = summary?.active_intents || [];
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="lowercase text-[#3B3A39] font-serif" style={{ fontFamily: "'DM Serif Display', ui-serif, Georgia, serif" }}>your watches</SheetTitle>
          <SheetDescription className="lowercase text-[#6B6A68]">i&apos;m only texting when one of these matches. that&apos;s the whole promise.</SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-3" data-testid="watches-sheet-content">
          {intents.length === 0 ? (
            <div className="text-sm text-[#6B6A68] leading-relaxed">
              no active watches yet. tell me your criteria in chat, then confirm the draft to start monitoring.
            </div>
          ) : (
            intents.map((intent, i) => {
              const c = intent.criteria || {};
              const locs = (c.locations || []).join(', ') || 'anywhere';
              const grossFloor = c.weekly_pay_floor?.amount;
              const pay = grossFloor ? `$${grossFloor}+/wk gross` : 'gross floor missing';
              return (
                <Card key={i} className="p-4 border-[#E6E6E6] rounded-xl bg-[#FBFBF9]" data-testid={`watch-card-${i}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#2F7D5C]" />
                    <span className="text-xs uppercase tracking-wide text-[#8B8A88]">active</span>
                  </div>
                  <div className="text-base text-[#3B3A39] font-medium">{c.specialty || 'unknown specialty'}</div>
                  <div className="text-sm text-[#6B6A68] mt-1">{locs}</div>
                  <div className="text-sm text-[#6B6A68]">{pay}</div>
                  {c.shift && <div className="text-xs text-[#8B8A88] mt-1">shift: {c.shift}</div>}
                </Card>
              );
            })
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

const WalletSheet = ({ open, onOpenChange, summary }) => {
  const credsSummary = summary?.credentials_summary || {};
  const completeness = credsSummary.completeness || {};
  const scorePct = typeof completeness.score_pct === 'number' ? completeness.score_pct : 0;
  const missing = completeness.missing || [];
  const count = credsSummary.count || 0;
  const expiringSoon = credsSummary.expiring_soon || [];
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="lowercase text-[#3B3A39] font-serif" style={{ fontFamily: "'DM Serif Display', ui-serif, Georgia, serif" }}>your wallet</SheetTitle>
          <SheetDescription className="lowercase text-[#6B6A68]">license, certs, resume. so APPLY is instant when I find a match.</SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-4" data-testid="wallet-sheet-content">
          <Card className="p-4 border-[#E6E6E6] rounded-xl bg-[#FBFBF9]">
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-xs uppercase tracking-wide text-[#8B8A88]">completeness</span>
              <span className="text-2xl font-serif text-[#3B3A39] tabular-nums" style={{ fontFamily: "'DM Serif Display', ui-serif, Georgia, serif" }}>{Math.round(scorePct)}%</span>
            </div>
            <div className="w-full h-1.5 bg-[#E6E6E6] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#2F7D5C] transition-all duration-500"
                style={{ width: `${Math.min(100, Math.max(0, scorePct))}%` }}
              />
            </div>
            <div className="text-xs text-[#6B6A68] mt-2">{count} credential{count === 1 ? '' : 's'} stored</div>
          </Card>

          {missing.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-[#8B8A88] mb-2">missing</div>
              <div className="flex flex-wrap gap-1.5">
                {missing.map((m, i) => (
                  <span key={i} className="px-2 py-1 text-xs rounded-full bg-white border border-[#E6E6E6] text-[#6B6A68] lowercase">{m.replace(/_/g, ' ')}</span>
                ))}
              </div>
            </div>
          )}

          {expiringSoon.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-[#8B8A88] mb-2">expiring soon</div>
              <div className="space-y-2">
                {expiringSoon.map((c, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-[#FEF6E7] border border-[#F0DDA5]">
                    <span className="text-sm text-[#3B3A39]">{c.type}</span>
                    <span className="text-xs text-[#8B6914]">{c.days_left}d</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <a
            href="/wallet"
            data-testid="wallet-manage-link"
            className="flex items-center justify-between px-4 py-3 rounded-xl border border-[#E6E6E6] bg-white hover:bg-[#F0EFEA] transition-colors text-sm text-[#3B3A39]"
          >
            <span className="lowercase">manage all credentials</span>
            <ArrowRight className="h-4 w-4 text-[#8B8A88]" />
          </a>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ============================================================================
// Rich Attachments — inline chat cards
// ============================================================================
const JobMatchCard = ({ attachment, onAction }) => {
  const pay = attachment.gross_weekly ? `$${attachment.gross_weekly.toLocaleString()}/wk gross` : 'gross pay unknown';
  return (
    <Card
      data-testid={`job-match-card-${attachment.index}`}
      className="mt-3 p-4 bg-white border border-[#E6E6E6] rounded-xl hover:border-[#A8A7A5] transition-colors shadow-sm"
    >
      <div className="flex items-start gap-3">
        <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#3B3A39] text-white text-sm font-semibold flex items-center justify-center tabular-nums">
          {attachment.index}
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-[15px] text-[#3B3A39] leading-tight">{attachment.title}</h3>
          <div className="text-sm text-[#6B6A68] mt-1 space-y-0.5">
            <div>{attachment.location}</div>
            <div className="tabular-nums">{pay}</div>
            {attachment.start_date && attachment.start_date !== 'unknown' && (
              <div className="text-xs text-[#8B8A88]">start {attachment.start_date}{attachment.length ? ` · ${attachment.length}` : ''}</div>
            )}
            <div className="text-xs text-[#8B8A88]">approved Care Team Solutions · supplied through Staffing Referrals</div>
            <div className="text-xs text-[#8B8A88]">shift: {attachment.shift || 'unknown'}</div>
          </div>
          {attachment.fit_reasons && attachment.fit_reasons.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {attachment.fit_reasons.slice(0, 3).map((r, i) => (
                <span key={i} className="px-2 py-0.5 text-[10px] rounded-full bg-[#F0EFEA] text-[#6B6A68] lowercase">{r}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {attachment.actions && attachment.actions.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {attachment.actions.map((action, i) => (
            <Button
              key={i}
              size="sm"
              data-testid={`job-action-${action.label.toLowerCase()}-${attachment.index}`}
              onClick={() => onAction(action)}
              variant={action.label === 'APPLY' ? 'default' : 'outline'}
              className={
                action.label === 'APPLY'
                  ? 'h-8 px-3 text-xs bg-[#3B3A39] text-white hover:bg-[#2C2B2A] rounded-lg font-medium'
                  : 'h-8 px-3 text-xs border-[#E6E6E6] text-[#6B6A68] hover:bg-[#F0EFEA] rounded-lg'
              }
            >
              {action.label}
            </Button>
          ))}
        </div>
      )}
      {attachment.source_url && (
        <a href={attachment.source_url} target="_blank" rel="noreferrer" className="mt-3 inline-block text-xs text-[#2F7D5C] underline">
          view source
        </a>
      )}
    </Card>
  );
};

const IntentConfirmCard = ({ attachment, onChip }) => (
  <Card
    data-testid="intent-confirm-card"
    className="mt-3 p-4 bg-white border border-[#E6E6E6] rounded-xl shadow-sm"
  >
    <div className="text-sm text-[#6B6A68] mb-3 lowercase">{attachment.prompt}</div>
    <div className="space-y-3">
      {(attachment.chips || []).map((chip, i) => (
        <div key={i}>
          <div className="text-xs uppercase tracking-wide text-[#8B8A88] mb-1.5">{chip.label}</div>
          {chip.options ? (
            <div className="flex flex-wrap gap-1.5">
              {chip.options.map((opt, j) => (
                <button
                  key={j}
                  data-testid={`chip-${chip.field}-${j}`}
                  onClick={() => onChip(chip.field, opt)}
                  className="px-3 py-1 text-xs rounded-full bg-[#F0EFEA] hover:bg-[#3B3A39] hover:text-white text-[#3B3A39] transition-colors"
                >
                  {opt}
                </button>
              ))}
            </div>
          ) : (
            <Input
              data-testid={`chip-input-${chip.field}`}
              placeholder={chip.placeholder || ''}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                  onChip(chip.field, e.currentTarget.value.trim());
                }
              }}
              className="text-sm h-9 border-[#E6E6E6] rounded-lg"
            />
          )}
        </div>
      ))}
    </div>
  </Card>
);

const CredentialAddCard = ({ attachment, onAction }) => (
  <Card
    data-testid={`credential-add-card-${attachment.type}`}
    className="mt-3 p-4 bg-white border border-[#E6E6E6] rounded-xl shadow-sm"
  >
    <div className="flex items-center gap-2 mb-3">
      <Shield className="h-4 w-4 text-[#6B6A68]" strokeWidth={1.8} />
      <span className="text-sm font-medium text-[#3B3A39] lowercase">{attachment.label}</span>
    </div>
    <div className="flex flex-wrap gap-1.5">
      {(attachment.actions || []).map((action, i) => {
        const Icon = action.kind === 'file_picker' && action.capture === 'environment'
          ? Camera
          : action.kind === 'file_picker'
          ? Upload
          : null;
        return (
          <Button
            key={i}
            size="sm"
            variant="outline"
            data-testid={`credential-action-${i}`}
            onClick={() => onAction(action)}
            className="h-8 px-3 text-xs border-[#E6E6E6] text-[#3B3A39] hover:bg-[#F0EFEA] rounded-lg gap-1.5"
          >
            {Icon && <Icon className="h-3 w-3" strokeWidth={1.8} />}
            {action.label}
          </Button>
        );
      })}
    </div>
  </Card>
);

// ============================================================================
// Share Sheet — kept from prior version
// ============================================================================
const ShareSheet = ({ onClose }) => {
  const shareUrl = 'https://heytraveler.ai';
  const shareText = "found this — a recruiter that only texts when there's a real match. no spam:";

  const handleShare = async (method) => {
    const fullText = `${shareText} ${shareUrl}`;
    if (method === 'native' && navigator.share) {
      try { await navigator.share({ title: 'HeyTraveler', text: shareText, url: shareUrl }); } catch { /* noop */ }
    } else if (method === 'sms') {
      window.open(`sms:?body=${encodeURIComponent(fullText)}`);
    } else if (method === 'copy') {
      await navigator.clipboard.writeText(fullText);
    }
    onClose();
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }} onClick={e => e.stopPropagation()} className="w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl p-6 pb-8 shadow-xl">
        <div className="w-10 h-1 bg-[#E6E6E6] rounded-full mx-auto mb-4 sm:hidden" />
        <h3 className="text-lg font-semibold text-[#3B3A39] mb-1 lowercase">know another traveler?</h3>
        <p className="text-sm text-[#6B6A68] mb-5 lowercase">share heytraveler. no spam, ever.</p>
        <div className="space-y-3">
          {navigator.share && (
            <Button data-testid="share-native-btn" onClick={() => handleShare('native')} className="w-full h-12 bg-[#3B3A39] text-white hover:bg-[#2C2B2A] rounded-xl justify-start gap-3 text-base">
              <Share2 className="h-5 w-5" /> share
            </Button>
          )}
          <Button data-testid="share-sms-btn" onClick={() => handleShare('sms')} variant="outline" className="w-full h-12 border-[#E6E6E6] text-[#3B3A39] hover:bg-[#F5F5F3] rounded-xl justify-start gap-3 text-base">
            <MessageCircle className="h-5 w-5" /> text a friend
          </Button>
          <Button data-testid="share-copy-btn" onClick={() => handleShare('copy')} variant="outline" className="w-full h-12 border-[#E6E6E6] text-[#3B3A39] hover:bg-[#F5F5F3] rounded-xl justify-start gap-3 text-base">
            <ExternalLink className="h-5 w-5" /> copy link
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ============================================================================
// App — the whole chat surface
// ============================================================================
function App() {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [needsSmsConsent, setNeedsSmsConsent] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [summary, setSummary] = useState(null);
  const [openSheet, setOpenSheet] = useState(null); // 'goal' | 'watches' | 'wallet' | null
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const sendMessageRef = useRef(null);
  const [pendingCredType, setPendingCredType] = useState(null);
  const initialMessageRef = useRef(new URLSearchParams(window.location.search).get('message'));

  // -------- Load history + summary on mount --------
  const loadSummary = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/me/summary`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
        if (data.traveler?.opted_in_sms) setSmsOptIn(true);
      }
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/chat/history`, { credentials: 'include' });
        if (!cancelled && res.ok) {
          const data = await res.json();
          const conv = (data.conversation || []).filter(m => m.role === 'user' || m.role === 'assistant');
          if (conv.length > 0) {
            setMessages(conv.map(m => ({
              role: m.role,
              content: m.content,
              timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
            })));
            await loadSummary();
          } else {
            // Empty history — plant the greeting bubble locally so the UI never shows blank.
            setMessages([{
              role: 'assistant',
              content: INTRO_MESSAGE,
              timestamp: new Date(),
            }]);
          }
        } else if (!cancelled) {
          // No cookie yet — plant greeting locally
          setMessages([{
            role: 'assistant',
            content: INTRO_MESSAGE,
            timestamp: new Date(),
          }]);
        }
      } catch {
        if (!cancelled) {
          setMessages([{
            role: 'assistant',
            content: INTRO_MESSAGE,
            timestamp: new Date(),
          }]);
        }
      } finally {
        if (!cancelled) {
          setHistoryLoaded(true);
          setTimeout(() => inputRef.current?.focus(), 100);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [loadSummary]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  // -------- Send message --------
  const sendMessage = async (overrideText) => {
    const text = (overrideText ?? inputValue).trim();
    if (!text) return;

    const userMessage = { role: 'user', content: text, timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: text,
          sms_opt_in: smsOptIn,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }),
      });
      const data = await response.json();
      if (data.needs_sms_consent && !smsOptIn) {
        setNeedsSmsConsent(true);
      }

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.message,
        timestamp: new Date(),
        message_kind: data.message_kind,
        attachments: [
          ...(data.job_cards || []).map((job, index) => ({
            ...job,
            kind: 'job_match_card',
            index: index + 1,
            actions: [
              { label: 'MORE', kind: 'chat_prefill', payload: `MORE ${job.job_id}` },
              { label: 'SAVE', kind: 'feedback', feedback: 'save', job_id: job.job_id },
              { label: 'PASS', kind: 'feedback', feedback: 'pass', job_id: job.job_id },
              { label: 'ASK TO APPLY', kind: 'application_request', job_id: job.job_id },
            ],
          })),
          ...(data.attachments || []).filter(att => att.kind !== 'job_match_card'),
        ],
        suggested_actions: data.suggested_actions || [],
        // Legacy fallback fields for old-style rendering
        job: data.has_job_match ? data.job : null,
        jobs: data.has_job_match && data.jobs ? data.jobs : null,
      }]);

      // Refresh summary so pills stay in sync
      loadSummary();
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "give me a sec, my brain glitched. try again in a minute?",
        timestamp: new Date(),
      }]);
    } finally {
      setIsTyping(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };
  sendMessageRef.current = sendMessage;

  // The home-page prompt is intentionally carried into the same concierge
  // session rather than creating a separate intake flow.
  useEffect(() => {
    const initialMessage = initialMessageRef.current;
    if (!historyLoaded || !initialMessage) return;
    initialMessageRef.current = null;
    window.history.replaceState({}, '', '/concierge');
    sendMessageRef.current(initialMessage);
  }, [historyLoaded]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleSmsOptInChange = async (checked) => {
    setSmsOptIn(checked);
    if (checked && needsSmsConsent) {
      setNeedsSmsConsent(false);
      try {
        await fetch(`${BACKEND_URL}/api/chat/enable-sms`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
      } catch { /* silent */ }
    }
  };

  // -------- Attachment action handlers --------
  const handleJobAction = async (action) => {
    if (action.kind === 'chat_prefill') {
      sendMessage(action.payload);
    } else if (action.kind === 'feedback') {
      const response = await fetch(`${BACKEND_URL}/api/watches/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ job_id: action.job_id, feedback: action.feedback }),
      });
      if (response.ok) sendMessage(action.feedback === 'pass' ? 'show me something else' : 'saved that match');
    } else if (action.kind === 'application_request') {
      const response = await fetch(`${BACKEND_URL}/api/submissions/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ job_id: action.job_id, method: 'web' }),
      });
      const data = await response.json();
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.ok ? data.message : "I couldn't create that review request. no application was submitted.",
        timestamp: new Date(),
      }]);
    }
  };

  const confirmWatch = async () => {
    const response = await fetch(`${BACKEND_URL}/api/watches/confirm`, {
      method: 'POST', credentials: 'include',
    });
    const data = await response.json();
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: response.ok
        ? "confirmed. monitoring is on. SMS alerts still require explicit consent and a verified phone."
        : `I can't start it yet: ${(data.detail?.missing_fields || []).join(', ') || 'the watch is incomplete'}.`,
      timestamp: new Date(),
    }]);
    loadSummary();
  };

  const handleChip = (field, value) => {
    // Send the field value directly as chat input, semantic
    const map = {
      specialty: `specialty: ${value}`,
      locations: `location: ${value}`,
      min_gross_weekly: `minimum gross weekly pay: ${value}`,
    };
    sendMessage(map[field] || value);
  };

  const handleCredAction = (action) => {
    if (action.kind === 'chat_prefill') {
      sendMessage(action.payload);
    } else if (action.kind === 'file_picker') {
      setPendingCredType(action.credential_type || null);
      if (fileInputRef.current) {
        fileInputRef.current.accept = action.accept || '*/*';
        if (action.capture) fileInputRef.current.setAttribute('capture', action.capture);
        else fileInputRef.current.removeAttribute('capture');
        fileInputRef.current.click();
      }
    }
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !pendingCredType) return;
    // Upload via /api/me/credentials with multipart
    const fd = new FormData();
    fd.append('file', file);
    fd.append('type', pendingCredType);
    try {
      const res = await fetch(`${BACKEND_URL}/api/me/credentials`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (res.ok) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `saved your ${pendingCredType.replace('_', ' ')}. that's stored — APPLY will be instant when I find a match.`,
          timestamp: new Date(),
        }]);
        loadSummary();
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: "couldn't save that one. try again or skip for now.",
          timestamp: new Date(),
        }]);
      }
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "couldn't save that one. try again or skip for now.",
        timestamp: new Date(),
      }]);
    } finally {
      setPendingCredType(null);
      e.target.value = '';
    }
  };

  // -------- Render helpers --------
  const goalLabel = summary?.active_goal ? 'goal' : 'goal';
  const goalValue = summary?.active_goal ? '·' : null;
  const watchesCount = summary?.active_intents?.length ?? 0;
  const walletScore = summary?.credentials_summary?.completeness?.score_pct;
  const walletPct = typeof walletScore === 'number' ? `${Math.round(walletScore)}%` : null;

  return (
    <main className="min-h-screen ht-ambient-bg ht-noise flex flex-col">
      {/* Sticky Header — Alex wordmark + ambient pills */}
      <header className="sticky top-0 z-30 border-b border-[#E6E6E6]/60 bg-[#FBFBF9]/85 backdrop-blur-md">
        <div className="mx-auto w-full max-w-[640px] px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-[#3B3A39] text-white flex items-center justify-center text-xs font-semibold" data-testid="alex-avatar">A</div>
            <h1
              data-testid="alex-wordmark"
              className="text-xl font-serif lowercase text-[#3B3A39] tracking-[-0.01em]"
              style={{ fontFamily: "'DM Serif Display', ui-serif, Georgia, serif" }}
            >
              alex
            </h1>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap justify-end">
            <AmbientPill icon={Target} label={goalLabel} value={goalValue} onClick={() => setOpenSheet('goal')} testId="pill-goal" />
            <AmbientPill icon={Eye} label="watches" value={watchesCount} onClick={() => setOpenSheet('watches')} testId="pill-watches" />
            {ALPHA_DOCUMENTS_ENABLED && (
              <AmbientPill icon={Shield} label="wallet" value={walletPct} onClick={() => setOpenSheet('wallet')} testId="pill-wallet" />
            )}
            <button
              onClick={() => setShowShare(true)}
              data-testid="share-btn"
              className="ml-1 p-1.5 rounded-full hover:bg-white/70 text-[#6B6A68] hover:text-[#3B3A39] transition-colors"
              aria-label="Share"
            >
              <Share2 className="h-4 w-4" strokeWidth={1.8} />
            </button>
          </div>
        </div>
      </header>

      {/* Chat surface — centered 640px column on desktop, full-screen on mobile */}
      <div className="flex-1 flex flex-col w-full max-w-[640px] mx-auto">
        <div
          ref={scrollRef}
          data-testid="chat-scroll"
          className="flex-1 overflow-y-auto px-4 sm:px-6 py-6 space-y-3"
          style={{ paddingBottom: '140px' }}
        >
          {!historyLoaded ? (
            <div className="flex justify-center pt-16 text-sm text-[#8B8A88] lowercase">loading...</div>
          ) : (
            <AnimatePresence initial={false}>
              {messages.map((message, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[85%] sm:max-w-[78%]`}>
                    <div
                      data-testid={message.role === 'user' ? 'msg-user' : 'msg-ai'}
                      className={`px-4 py-3 ${message.role === 'user'
                        ? 'bg-[#3B3A39] text-white rounded-2xl rounded-tr-md shadow-sm'
                        : 'bg-white text-[#3B3A39] rounded-2xl rounded-tl-md shadow-sm border border-[#E6E6E6]'
                      }`}
                    >
                      <p className="text-[15px] leading-6 whitespace-pre-wrap">{message.content}</p>
                    </div>

                    {/* Phase 1c: rich attachments rendered outside bubble */}
                    {message.role === 'assistant' && message.attachments && message.attachments.length > 0 && (
                      <div>
                        {message.attachments.map((att, i) => {
                          if (att.kind === 'job_match_card') return <JobMatchCard key={i} attachment={att} onAction={handleJobAction} />;
                          if (att.kind === 'intent_confirm_prompt') return <IntentConfirmCard key={i} attachment={att} onChip={handleChip} />;
                          if (ALPHA_DOCUMENTS_ENABLED && att.kind === 'credential_add_prompt') return <CredentialAddCard key={i} attachment={att} onAction={handleCredAction} />;
                          return null;
                        })}
                      </div>
                    )}

                    {message.role === 'assistant' && message.suggested_actions?.some(action => action.action === 'confirm_watch') && (
                      <Button onClick={confirmWatch} className="mt-2 h-9 bg-[#2F7D5C] text-white rounded-lg">
                        confirm this watch
                      </Button>
                    )}

                    {/* Legacy fallback for old-format job cards */}
                    {message.role === 'assistant'
                      && (!message.attachments || message.attachments.length === 0)
                      && message.jobs && message.jobs.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {message.jobs.map((job, jobIdx) => (
                            <Card key={job._id || jobIdx} className="p-4 bg-white border border-[#E6E6E6] rounded-xl shadow-sm">
                              <h3 className="font-semibold text-[15px] text-[#3B3A39]">{job.title}</h3>
                              <div className="text-sm text-[#6B6A68] mt-1">
                                <div>{job.location}</div>
                                <div>{job.pay}</div>
                              </div>
                              <Button
                                size="sm"
                                onClick={() => sendMessage(`APPLY ${jobIdx + 1}`)}
                                className="mt-3 w-full h-8 text-xs bg-[#3B3A39] text-white hover:bg-[#2C2B2A] rounded-lg"
                              >
                                Ask to apply
                              </Button>
                            </Card>
                          ))}
                        </div>
                      )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          )}

          {/* Inline SMS consent — only when backend flagged it */}
          {needsSmsConsent && !smsOptIn && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex justify-center pt-2"
            >
              <div className="bg-[#F0EFEA]/80 backdrop-blur-sm px-4 py-3 rounded-xl border border-[#2F7D5C] shadow-[0_0_10px_rgba(47,125,92,0.3)] flex items-start gap-3 max-w-[85%]">
                <Checkbox
                  id="sms-opt-in"
                  data-testid="sms-consent-checkbox"
                  checked={smsOptIn}
                  onCheckedChange={handleSmsOptInChange}
                  className="mt-0.5"
                />
                <label htmlFor="sms-opt-in" className="text-xs text-[#6B6A68] leading-relaxed cursor-pointer select-none">
                  ok to receive occasional sms about matches. reply STOP anytime.
                </label>
              </div>
            </motion.div>
          )}

          {/* Typing indicator */}
          {isTyping && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start" data-testid="typing-indicator">
              <div className="px-4 py-3 bg-white rounded-2xl rounded-tl-md shadow-sm border border-[#E6E6E6]">
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-[#A8A7A5] rounded-full" style={{ animation: 'dot 1.4s ease-in-out 0s infinite' }} />
                  <span className="w-2 h-2 bg-[#A8A7A5] rounded-full" style={{ animation: 'dot 1.4s ease-in-out 0.2s infinite' }} />
                  <span className="w-2 h-2 bg-[#A8A7A5] rounded-full" style={{ animation: 'dot 1.4s ease-in-out 0.4s infinite' }} />
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Composer — fixed at bottom */}
        <div className="fixed bottom-0 left-0 right-0 border-t border-[#E6E6E6]/60 bg-[#FBFBF9]/95 backdrop-blur-md z-20">
          <div className="mx-auto w-full max-w-[640px] px-4 sm:px-6 py-3">
            <div className="flex gap-2 items-center bg-white border-2 border-[#E6E6E6] rounded-2xl px-3 py-1.5 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.18)] hover:border-[#A8A7A5] transition-colors">
              <Input
                ref={inputRef}
                data-testid="chat-input"
                type="text"
                placeholder="message alex..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isTyping}
                className="flex-1 border-none bg-transparent text-[15px] text-[#3B3A39] placeholder:text-[#A8A7A5] focus-visible:ring-0 focus-visible:ring-offset-0 px-1"
              />
              <Button
                data-testid="chat-send"
                onClick={() => sendMessage()}
                disabled={!inputValue.trim() || isTyping}
                className="h-9 w-9 rounded-full bg-[#3B3A39] text-white hover:bg-[#2C2B2A] hover:scale-105 active:scale-95 transition-all flex-shrink-0"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden file input for credential uploads */}
      {ALPHA_DOCUMENTS_ENABLED && (
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={handleFileSelected}
          data-testid="credential-file-input"
        />
      )}

      {/* Sheets */}
      <GoalSheet open={openSheet === 'goal'} onOpenChange={(o) => setOpenSheet(o ? 'goal' : null)} summary={summary} />
      <WatchesSheet open={openSheet === 'watches'} onOpenChange={(o) => setOpenSheet(o ? 'watches' : null)} summary={summary} />
      {ALPHA_DOCUMENTS_ENABLED && (
        <WalletSheet open={openSheet === 'wallet'} onOpenChange={(o) => setOpenSheet(o ? 'wallet' : null)} summary={summary} />
      )}

      <AnimatePresence>{showShare && <ShareSheet onClose={() => setShowShare(false)} />}</AnimatePresence>
    </main>
  );
}

export default App;
